import React from 'react';
import PropTypes from 'prop-types';

import { snakeCase } from 'lodash';
import displayName from 'component-lib/displayName';
import { FormattedMessage } from 'component-utilities/intl';

import PrimaryButton from 'v-c/Buttons/PrimaryButton';
import SecondaryButton from 'v-c/Buttons/SecondaryButton';

import {
    TableView,
    TableHeader,
    TableBody,
    TableRow,
    CheckboxCell,
    TextCell,
    DataCell,
} from 'v-c/TableView';

import ModalContainer from '../ModalContainer';
import ModalContentCenter from '../ModalContentCenter';
import ModalHeader from '../ModalHeader';
import ModalButtons from '../ModalButtons';
import ModalText from '../ModalText';
import ModalTextDark from '../ModalTextDark';
import ModalButtonSpacer from '../ModalButtonSpacer';

import styles from '../Modals.scss';

export const ALL_CHECKBOX = 'allCheckbox';

export default function RolesModal({
    closeModal,
    bulkOptions,
    individualOptions,
    onClickCheckbox,
    isAllSelected,
    isBulkButtonDisabled,
    isUpdateButtonDisabled,
    preparedUserRoles,
    tempSelectedRoles,
}) {
    const BASE_KEY = 'components.modals.user_roles';

    return (
        <ModalContainer data-modal="roles" dataTags={{ 'data-modal': 'roles-update' }}>
            <ModalHeader onCloseClick={closeModal}>
                <FormattedMessage id={`${BASE_KEY}.title`} />
            </ModalHeader>

            <ModalContentCenter>
                <div className="roles-table-header">
                    <TableView tableId="roles-groups-status-table-header">
                        <TableHeader>
                            <TableRow index={0} rowId="table-header-row">
                                <CheckboxCell
                                    onClick={onClickCheckbox}
                                    isChecked={isAllSelected}
                                    indeterminate={!isAllSelected && !!tempSelectedRoles.length}
                                    type="small"
                                    cellId={ALL_CHECKBOX}
                                    data-action="all-checkbox"
                                />
                                <TextCell isFloatingCell cellId="names">
                                    <FormattedMessage id={`${BASE_KEY}.header`} />
                                </TextCell>
                            </TableRow>
                        </TableHeader>
                    </TableView>
                </div>
                <div className="roles-table-wrapper">
                    <TableView tableId="roles-groups-status-table">
                        <TableBody>
                            {preparedUserRoles.map(({ id, forceHover, isDisabled, isChecked, isProtected }, idx) => (
                                <TableRow
                                    key={id}
                                    index={idx}
                                    rowId={id}
                                    data-action={`select-${id}`}
                                    className="row-with-checkbox"
                                    onClick={() => onClickCheckbox(id, !isChecked)}
                                    status={isProtected ? TableRow.rowStatuses.UNSELECTABLE : null}
                                >
                                    <CheckboxCell
                                        isChecked={isChecked}
                                        forceHover={forceHover}
                                        isDisabled={isDisabled}
                                        cellId={id}
                                        type="small"
                                    />
                                    <DataCell additionalClasses="bulk-role-text-cell" cellId={id} isFloatingCell>
                                        <ModalTextDark>
                                            <FormattedMessage id={`${BASE_KEY}.${snakeCase(id)}.title`} />
                                        </ModalTextDark>
                                        <ModalText>
                                            <FormattedMessage id={`${BASE_KEY}.${snakeCase(id)}.description`} />
                                        </ModalText>
                                    </DataCell>
                                </TableRow>
                            ),)}
                        </TableBody>
                    </TableView>
                </div>
            </ModalContentCenter>

            {bulkOptions && (
                <ModalButtons>
                    <span className={styles['buttons-left']}>
                        <PrimaryButton
                            className={styles[`button-primary${isBulkButtonDisabled ? '-disabled' : ''}`]}
                            onClick={() => bulkOptions.addRoles(tempSelectedRoles)}
                            data-action="add"
                        >
                            <FormattedMessage id={`${BASE_KEY}.buttons.add`} />
                        </PrimaryButton>
                        <span className={styles['modal-button-spacer']} />
                        <SecondaryButton
                            className={
                                styles[`button-secondary${isBulkButtonDisabled ? '-disabled' : '-warning'}`]
                            }
                            onClick={() => bulkOptions.removeRoles(tempSelectedRoles)}
                            data-action="remove"
                        >
                            <FormattedMessage id={`${BASE_KEY}.buttons.remove`} />
                        </SecondaryButton>
                    </span>
                    <span className={styles['buttons-right']}>
                        <SecondaryButton
                            data-action="cancel"
                            className={styles['button-secondary']}
                            onClick={closeModal}
                        >
                            <FormattedMessage id={`${BASE_KEY}.buttons.cancel`} />
                        </SecondaryButton>
                    </span>
                </ModalButtons>
            )}

            {individualOptions && (
                <ModalButtons>
                    <PrimaryButton
                        data-action="update"
                        onClick={() => individualOptions.updateRoles(tempSelectedRoles)}
                        disabled={isUpdateButtonDisabled}
                    >
                        <FormattedMessage id={`${BASE_KEY}.buttons.update`} />
                    </PrimaryButton>
                    <ModalButtonSpacer />
                    <SecondaryButton
                        data-action="cancel"
                        onClick={() => closeModal({ from: 'RolesModal/cancel' })}
                    >
                        <FormattedMessage id={`${BASE_KEY}.buttons.cancel`} />
                    </SecondaryButton>
                </ModalButtons>
            )}
        </ModalContainer>
    );
}

RolesModal.displayName = displayName('Modals/RolesModal');

RolesModal.propTypes = {
    bulkOptions: PropTypes.shape({
        addRoles: PropTypes.func.isRequired,
        removeRoles: PropTypes.func.isRequired,
    }),
    individualOptions: PropTypes.shape({
        updateRoles: PropTypes.func.isRequired,
    }),
    closeModal: PropTypes.func.isRequired,
    onClickCheckbox: PropTypes.func.isRequired,
    isAllSelected: PropTypes.bool.isRequired,
    isBulkButtonDisabled: PropTypes.bool.isRequired,
    isUpdateButtonDisabled: PropTypes.bool.isRequired,
    preparedUserRoles: PropTypes.array.isRequired,
    tempSelectedRoles: PropTypes.array.isRequired,
};

RolesModal.defaultProps = {
    bulkOptions: null,
    individualOptions: null,
};
